import React from 'react';
import { Card, Flex, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import styles from '../ModelDetail.module.scss';
import { GetAiModelDetailResponseData } from '@manifest-cyber/types/interface/contracts/model-analysis/get.contract';
import { DateTime } from 'luxon';

interface ModelDetailsCardProps {
  modelData: GetAiModelDetailResponseData;
  onShowPapers: () => void;
}

export const ModelDetailsCard: React.FC<ModelDetailsCardProps> = ({
  modelData,
  onShowPapers,
}) => {
  const { t } = useTranslation();

  const calculateModelAge = (dateValue?: Date | string): string => {
    if (!dateValue) return t('mosaic.modelDetail.fallbacks.unknownDate');

    const date = DateTime.fromJSDate(
      dateValue instanceof Date ? dateValue : new Date(dateValue),
    );
    if (!date.isValid) return t('global.unknown');

    const now = DateTime.now();
    const diff = now.diff(date, ['years', 'months', 'days']).toObject();

    const years = Math.floor(diff.years ?? 0);
    const months = Math.floor(diff.months ?? 0);
    const days = Math.floor(diff.days ?? 0);

    const parts = [];
    if (years > 0) parts.push(t('mosaic.modelDetail.year', { count: years }));
    if (months > 0) parts.push(t('mosaic.modelDetail.month', { count: months }));
    if (days > 0) parts.push(t('mosaic.modelDetail.day', { count: days }));

    return parts.length > 0 ? parts.join(', ') : t('mosaic.modelDetail.justCreated');
  };

  const formatDate = (dateValue?: Date | string): string => {
    if (!dateValue) return t('mosaic.modelDetail.fallbacks.unknownDate');

    const date = DateTime.fromJSDate(
      dateValue instanceof Date ? dateValue : new Date(dateValue),
    );
    if (!date.isValid) return t('mosaic.modelDetail.fallbacks.unknownDate');

    return date.toLocaleString(DateTime.DATE_FULL);
  };

  return (
    <Card shadow="sm" padding="lg" withBorder className={styles.cardContainer}>
      <Title order={3} mb="md">
        {t('mosaic.modelDetail.cards.details.title')}
      </Title>
      <Flex direction="row" justify="space-between">
        <Flex direction="column" gap={16} className={styles.columnContainer}>
          <DetailInfoItem
            title={t('mosaic.modelDetail.cards.details.fields.modelAge')}
            value={calculateModelAge(modelData.dateCreated)}
          />
          <DetailInfoItem
            title={t('mosaic.modelDetail.cards.details.fields.license')}
            value={modelData.license || t('global.unknown')}
          />
          <DetailInfoItem
            title={t('mosaic.modelDetail.cards.details.fields.architecture')}
            value={modelData.architecture || t('global.unknown')}
          />
          <DetailInfoItem
            title={t('mosaic.modelDetail.cards.details.fields.version')}
            value={modelData.version || t('global.unknown')}
          />
          <DetailInfoItem
            title={t('mosaic.modelDetail.cards.details.fields.purl')}
            value={modelData.packageUrl || t('global.unknown')}
          />
          <Flex direction="column" gap={4}>
            <p className={styles.detailInfoItemTitle}>
              {t('mosaic.modelDetail.cards.details.fields.arxivePapers')}
            </p>
            <p
              className={`${styles.detailInfoItemLink} ${styles.paperLink}`}
              onClick={onShowPapers}
            >
              {t('mosaic.modelDetail.cards.details.fields.papers', {
                count: modelData.papers?.length || 0,
              })}
            </p>
          </Flex>
        </Flex>
        <Flex direction="column" gap={16} className={styles.columnContainer}>
          <DetailInfoItem
            title={t('mosaic.modelDetail.cards.details.fields.lastUpdated')}
            value={formatDate(modelData.dateModified)}
          />
        </Flex>
      </Flex>
    </Card>
  );
};

interface DetailInfoItemProps {
  title: string;
  value: string;
}

const DetailInfoItem: React.FC<DetailInfoItemProps> = ({ title, value }) => (
  <Flex direction="column" gap={4}>
    <p className={styles.detailInfoItemTitle}>{title}</p>
    <p className={styles.detailInfoItemValue}>{value}</p>
  </Flex>
);
